import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../api";
import CurrencyInput from "./CurrencyInput";

export default function PropertyChecklist({ property, prediction, reviewId, onSave, agentAnalysis, aiPrediction, computedValues, onPropertyUpdate }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("");  // "", "saving", "saved", "error"
  const saveTimeoutRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track if this is the first load
  const [generatingSummary, setGeneratingSummary] = useState(false); // Track AI summary generation

  // Modal state for MIS/Retired transitions
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(""); // "mis" or "retired"
  const [statusComment, setStatusComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Status info for MIS/Retired
  const [statusInfo, setStatusInfo] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);

  const [checklist, setChecklist] = useState({
    // Taxes
    taxes_confirmed: false,
    taxes_amount: property?.monthly_tax || "",
    taxes_notes: "",
    taxes_acris_link: "",
    taxes_abatement_enabled: false, // Checkbox for tax abatement
    taxes_abatement_type: "", // 421a, j-51, condo_abatement, other
    taxes_abatement_type_other: "", // custom type when "other"
    taxes_abatement_with: "", // taxes with abatement $/mo
    taxes_abatement_without: "", // taxes without abatement $/mo

    // Square Footage
    sqft_confirmed: false,
    sqft_amount: property?.sqft || "", // Interior area
    sqft_exterior_enabled: false, // Checkbox for exterior area
    sqft_exterior_amount: "", // Exterior area
    sqft_composite: "", // Calculated: interior + (exterior/3)

    // Convertible Bedrooms
    convertible_enabled: false,
    convertible_bedrooms: "",

    // Floor Plan
    floorplan_status: "", // "" = not verified, "available" = has floor plan, "not_available" = confirmed no floor plan
    floorplan_link: property?.floor_plan_url || "",

    // Rent Prediction
    rent_prediction_mode_low: "", // "" = not set, "ml", "ai", "current_tenant", "custom"
    rent_prediction_mode_high: "", // "" = not set, "ml", "ai", "current_tenant", "custom"
    rent_prediction_range_low: "",
    rent_prediction_range_high: "",

    // Commission
    commission_percentage: "",

    // Tenant Occupied
    tenant_occupied: "", // "" = not set, "yes" or "no"
    tenant_current_rent: "",
    tenant_lease_type: "", // "" = not set, "month_to_month" or "fixed_term"
    tenant_lease_end_date: "", // Date when fixed-term lease ends
    tenant_rent_periods: [], // [{rent: "", from: "", to: ""}] for multi-period leases
    occupancy_notes: "", // Notes when tenant_occupied = "no"

    // Broker Notes
    broker_confirmed: false,
    broker_notes: "",
    broker_assessments: "",
    assessment_enabled: false, // Toggle for assessment
    assessment_cost: "", // Monthly cost amount
    assessment_type: "", // "monthly" (with end date) or "forever" (ongoing)
    assessment_end_date: "", // End date for monthly assessments
    assessment_credit_pct: "", // Credit discount percentage (e.g., 50)
    broker_building_financials: "",
    broker_negotiability: "",

    // Rental Application
    rental_application_available: "", // "" = not set, "yes", "no"
    rental_application_link: "",

    // Rental Fees
    rental_fees_confirmed: false,
    rental_fees: "",

    // Local Law 11
    local_law_11_status: "", // "" = not verified, "yes" = applicable, "no" = not applicable
    local_law_11_date: "",
    local_law_11_notes: "",

    // Property Video
    property_video_status: "", // "" = not verified, "available" = has video, "not_available" = confirmed no video
    property_video_link: property?.video_url || "",

    // Co-op/Condop Special Fields (only shown for coop/condop)
    coop_sponsor_shares: false, // true if unit was approved as sponsor shares
    coop_sublet_policy: "", // "" = not set, "not_allowed", "allowed", "vesting_period", "sometimes"
    coop_sublet_vesting_details: "", // Details when vesting_period is selected
    coop_sublet_sometimes_numerator: "", // First number for "X out of Y"
    coop_sublet_sometimes_denominator: "", // Second number for "X out of Y"
    coop_international_buyers: "", // "" = N/A, "yes", "no"
    coop_llc_buyer: "", // "" = N/A, "yes", "no"
    coop_board_interview_rentals: "", // "" = N/A, "yes", "no"
    coop_board_interview_sales: "", // "" = N/A, "yes", "no"

    // Co-op Special Notes (kept for backwards compatibility, shown at end)
    coop_special_notes: "",

    // Other Costs (parking, storage, etc.)
    other_costs_confirmed: false,
    other_costs_items: [], // Array of {id, name, cost, included}

    // AI Summary - Split into three sections
    ai_summary_unit: "",
    ai_summary_building: "",
    ai_summary_location: "",
  });

  // Load checklist data on mount
  useEffect(() => {
    const loadChecklist = async () => {
      if (!reviewId) {
        console.log("No review_id provided");
        setLoading(false);
        return;
      }

      try {
        // Load checklist data
        const checklistData = await authFetch(`/sale-reviews/${reviewId}/checklist`);

        let loadedChecklist = null;
        if (checklistData.checklist_data && Object.keys(checklistData.checklist_data).length > 0) {
          loadedChecklist = checklistData.checklist_data;
        }

        // For co-op/condop properties, pre-populate sponsor_shares from listing data
        if (property?.sponsor_shares && (!loadedChecklist || !loadedChecklist.coop_sponsor_shares)) {
          loadedChecklist = { ...(loadedChecklist || {}), coop_sponsor_shares: true };
        }

        // For co-op/condop properties, pre-populate co-op fields from building-level policies
        const isCoopOrCondop = property?.property_type?.toLowerCase() === "coop" ||
                               property?.property_type?.toLowerCase() === "condop";
        if (isCoopOrCondop && property?.id && (!loadedChecklist || !loadedChecklist.coop_sublet_policy)) {
          try {
            const policyResp = await authFetch(`/listings/${property.id}/coop-policies`);
            const policies = policyResp?.coop_policies;
            if (policies && policies.sublet_policy) {
              const coopDefaults = {
                coop_sublet_policy: policies.sublet_policy || "",
                coop_sublet_vesting_details: policies.sublet_vesting_details || "",
                coop_sublet_sometimes_numerator: policies.sublet_sometimes_numerator || "",
                coop_sublet_sometimes_denominator: policies.sublet_sometimes_denominator || "",
                coop_international_buyers: policies.international_buyers || "",
                coop_llc_buyer: policies.llc_buyer || "",
                coop_board_interview_rentals: policies.board_interview_rentals || "",
                coop_board_interview_sales: policies.board_interview_sales || "",
                coop_special_notes: policies.special_notes || "",
              };
              loadedChecklist = { ...(loadedChecklist || {}), ...coopDefaults };
            }
          } catch (e) {
            // No building policies found, that's fine
            console.log("No building-level coop policies found");
          }
        }

        // Pre-populate ACRIS URL from realplus_urls if checklist doesn't have one
        if (checklistData.acris_url && (!loadedChecklist || !loadedChecklist.taxes_acris_link)) {
          loadedChecklist = { ...(loadedChecklist || {}), taxes_acris_link: checklistData.acris_url };
        }

        // Pre-populate Tax Abatement from realplus_listings if checklist doesn't have it
        if (checklistData.tax_abatement !== null && checklistData.tax_abatement !== undefined) {
          if (!loadedChecklist || loadedChecklist.taxes_abatement_enabled === undefined || loadedChecklist.taxes_abatement_enabled === false) {
            loadedChecklist = { ...(loadedChecklist || {}), taxes_abatement_enabled: checklistData.tax_abatement };
          }
        }
        // Pre-populate tax abatement description into notes field
        if (checklistData.tax_abatement_description && (!loadedChecklist || !loadedChecklist.taxes_notes)) {
          loadedChecklist = { ...(loadedChecklist || {}), taxes_notes: checklistData.tax_abatement_description };
        }

        // Pre-populate taxes_amount from property.monthly_tax if checklist doesn't have it
        if (property?.monthly_tax && (!loadedChecklist || !loadedChecklist.taxes_amount)) {
          loadedChecklist = { ...(loadedChecklist || {}), taxes_amount: property.monthly_tax };
        }

        // Pre-populate from listing_details (verified sqft, media, taxes)
        const ld = property?.listing_details;
        if (ld && Object.keys(ld).length > 0) {
          const ldDefaults = {};
          if (ld.sqft_verified && (!loadedChecklist || !loadedChecklist.sqft_confirmed)) {
            ldDefaults.sqft_confirmed = true;
            ldDefaults.sqft_amount = ld.sqft_verified;
          }
          if (ld.sqft_exterior && (!loadedChecklist || !loadedChecklist.sqft_exterior_enabled)) {
            ldDefaults.sqft_exterior_enabled = true;
            ldDefaults.sqft_exterior_amount = ld.sqft_exterior;
          }
          if (ld.floorplan_status && (!loadedChecklist || !loadedChecklist.floorplan_status)) {
            ldDefaults.floorplan_status = ld.floorplan_status;
          }
          if (ld.floorplan_link && (!loadedChecklist || !loadedChecklist.floorplan_link)) {
            ldDefaults.floorplan_link = ld.floorplan_link;
          }
          if (ld.video_status && (!loadedChecklist || !loadedChecklist.property_video_status)) {
            ldDefaults.property_video_status = ld.video_status;
          }
          if (ld.video_link && (!loadedChecklist || !loadedChecklist.property_video_link)) {
            ldDefaults.property_video_link = ld.video_link;
          }
          // "Taxes Confirmed" should reflect a successful tax extraction, not just the
          // legacy taxes_confirmed flag — the tax handler sets tax_bill.status='success'
          // (or 'no_data_verified' when DOF has nothing) but never writes taxes_confirmed.
          // Mirror the backend's definition (predictions.py / audit_verification.py).
          const taxConfirmedSignal =
            ld.taxes_confirmed ||
            ld.tax_bill?.status === "success" ||
            ld.tax_bill?.status === "no_data_verified";
          if (taxConfirmedSignal && (!loadedChecklist || !loadedChecklist.taxes_confirmed)) {
            ldDefaults.taxes_confirmed = true;
          }
          if (ld.tax_abatement_type && (!loadedChecklist || !loadedChecklist.taxes_abatement_type)) {
            ldDefaults.taxes_abatement_enabled = true;
            ldDefaults.taxes_abatement_type = ld.tax_abatement_type;
          }
          if (ld.tax_abatement_type_other && (!loadedChecklist || !loadedChecklist.taxes_abatement_type_other)) {
            ldDefaults.taxes_abatement_type_other = ld.tax_abatement_type_other;
          }
          if (ld.tax_abatement_with && (!loadedChecklist || !loadedChecklist.taxes_abatement_with)) {
            ldDefaults.taxes_abatement_with = ld.tax_abatement_with;
          }
          if (ld.tax_abatement_without && (!loadedChecklist || !loadedChecklist.taxes_abatement_without)) {
            ldDefaults.taxes_abatement_without = ld.tax_abatement_without;
          }
          if (ld.tax_abatement_verified_date && (!loadedChecklist || !loadedChecklist.taxes_abatement_date)) {
            ldDefaults.taxes_abatement_date = ld.tax_abatement_verified_date;
          }
          if (Object.keys(ldDefaults).length > 0) {
            loadedChecklist = { ...(loadedChecklist || {}), ...ldDefaults };
          }
        }

        if (loadedChecklist) {
          setChecklist(loadedChecklist);
          if (onSave) {
            onSave(loadedChecklist);
          }
        }

        // Set status and status_info
        setCurrentStatus(checklistData.status);
        if (checklistData.status_info) {
          setStatusInfo(checklistData.status_info);
        }

        setLoading(false);
        // Mark initial load as complete after a short delay to prevent auto-save
        setTimeout(() => {
          setIsInitialLoad(false);
        }, 100);
      } catch (error) {
        console.error("Error loading checklist:", error);
        setLoading(false);
        setTimeout(() => {
          setIsInitialLoad(false);
        }, 100);
      }
    };

    loadChecklist();
  }, [reviewId]);

  // Notify parent of checklist changes (for IRR modal and tenant rent display)
  useEffect(() => {
    if (onSave && !loading) {
      onSave(checklist);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklist, loading]);

  // Auto-save with debounce
  useEffect(() => {
    // Skip auto-save during initial load or if still loading
    if (!reviewId || loading || isInitialLoad) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout (2 seconds after user stops typing)
    setSaveStatus("pending");
    saveTimeoutRef.current = setTimeout(() => {
      saveChecklist();
    }, 2000);

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklist, reviewId, loading, isInitialLoad]);

  const saveChecklist = async () => {
    if (!reviewId) return;

    setSaveStatus("saving");

    try {
      const stats = completionStats();

      const response = await authFetch(`/sale-reviews/${reviewId}/checklist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          checklist_data: checklist,
          completed_items: stats.completed,
          total_items: stats.total,
          completion_pct: (stats.completed / stats.total) * 100
        })
      });

      setSaveStatus("saved");

      // Clear saved status after 2 seconds
      setTimeout(() => setSaveStatus(""), 2000);

      // If status changed, notify parent (for Kanban board update)
      if (response.status_changed && onSave) {
        onSave({ status: response.status });
      }

      // Reload property data to reflect any changes to tax/HOA
      if (onPropertyUpdate) {
        onPropertyUpdate();
      }

    } catch (error) {
      console.error("Error saving checklist:", error);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(""), 3000);
    }
  };

  const handleCheckboxChange = (field) => {
    setChecklist({ ...checklist, [field]: !checklist[field] });
  };

  const handleInputChange = (field, value) => {
    setChecklist({ ...checklist, [field]: value });
  };

  // Other Costs management
  const addOtherCostItem = () => {
    const newItem = {
      id: Date.now(),
      name: "",
      cost: "",
      payment_type: "included" // Default: "included", "financed", or "closing_costs"
    };
    setChecklist({
      ...checklist,
      other_costs_items: [...(checklist.other_costs_items || []), newItem]
    });
  };

  const removeOtherCostItem = (id) => {
    setChecklist({
      ...checklist,
      other_costs_items: checklist.other_costs_items.filter(item => item.id !== id)
    });
  };

  const updateOtherCostItem = (id, field, value) => {
    setChecklist({
      ...checklist,
      other_costs_items: checklist.other_costs_items.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    });
  };

  // Generate AI summary
  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const response = await authFetch(`/reviews/${reviewId}/generate-summary`, {
        method: 'POST'
      });

      if (response.summary) {
        // Parse the summary into three sections
        const summary = response.summary;

        // Extract sections using regex
        const unitMatch = summary.match(/UNIT INFO:\s*(.*?)(?=BUILDING INFO:|$)/s);
        const buildingMatch = summary.match(/BUILDING INFO:\s*(.*?)(?=BUILDING LOCATION INFO:|$)/s);
        const locationMatch = summary.match(/BUILDING LOCATION INFO:\s*(.*?)$/s);

        setChecklist(prev => ({
          ...prev,
          ai_summary_unit: unitMatch ? unitMatch[1].trim() : "",
          ai_summary_building: buildingMatch ? buildingMatch[1].trim() : "",
          ai_summary_location: locationMatch ? locationMatch[1].trim() : "",
        }));
      }
    } catch (error) {
      console.error("Error generating summary:", error);
    } finally {
      setGeneratingSummary(false);
    }
  };

  // Auto-generate summary when page loads if no summary exists yet
  useEffect(() => {
    // Don't run while loading or if already generating
    const hasAnySummary = checklist.ai_summary_unit || checklist.ai_summary_building || checklist.ai_summary_location;
    if (loading || generatingSummary || hasAnySummary) {
      return;
    }

    // Skip during initial load period
    if (isInitialLoad) {
      return;
    }

    // Auto-generate summary after initial load completes
    // Backend will check if description exists and return appropriate response
    const timer = setTimeout(() => {
      handleGenerateSummary();
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklist.ai_summary_unit, checklist.ai_summary_building, checklist.ai_summary_location, loading, generatingSummary, isInitialLoad]);

  const handleRentPredictionModeChange = (field, mode) => {
    // field is either "rent_prediction_mode_low" or "rent_prediction_mode_high"
    // mode is "ml", "ai", "current_tenant", or "custom"

    let updatedChecklist = { ...checklist, [field]: mode };
    const isLow = field === "rent_prediction_mode_low";
    const rangeField = isLow ? "rent_prediction_range_low" : "rent_prediction_range_high";

    // Auto-fill based on mode
    if (mode === "ml" && prediction?.predicted_rent) {
      updatedChecklist[rangeField] = prediction.predicted_rent.toString();
    } else if (mode === "ai" && (aiPrediction?.predicted_rent || agentAnalysis?.agent_adjusted_rent)) {
      updatedChecklist[rangeField] = (aiPrediction?.predicted_rent || agentAnalysis?.agent_adjusted_rent).toString();
    } else if (mode === "current_tenant" && checklist.tenant_current_rent) {
      updatedChecklist[rangeField] = checklist.tenant_current_rent;
    } else if (mode === "custom") {
      // Keep existing value for custom mode, allow editing
    }

    setChecklist(updatedChecklist);
  };

  const handleStatusChange = async (newStatus) => {
    if (!statusComment.trim()) {
      alert("Please provide a reason for this status change.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await authFetch(`/sale-reviews/${property.id}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: newStatus.toUpperCase(),
          reason: statusComment
        })
      });

      if (response.success) {
        // Redirect to Kanban board
        navigate("/");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const openModal = (type) => {
    setModalType(type);
    setStatusComment("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setStatusComment("");
    setModalType("");
  };

  const containerStyle = {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    // Fill the available height inside the sticky right column and scroll
    // internally; stickiness/height is owned by the right column wrapper now.
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
  };

  const headerStyle = {
    fontSize: "20px",
    fontWeight: "700",
    marginBottom: "20px",
    color: "#333",
    borderBottom: "2px solid #e0e0e0",
    paddingBottom: "10px",
  };

  const sectionStyle = {
    marginBottom: "20px",
    paddingBottom: "15px",
    borderBottom: "1px solid #f0f0f0",
  };

  const checkboxRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  };

  const labelStyle = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "6px",
    display: "block",
  };

  const inputStyle = {
    width: "100%",
    padding: "8px",
    fontSize: "13px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    marginBottom: "8px",
    boxSizing: "border-box", // Ensure padding doesn't cause overflow
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: "60px",
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box", // Ensure padding doesn't cause overflow
  };

  const linkButtonStyle = {
    fontSize: "11px",
    padding: "4px 8px",
    backgroundColor: "#1976d2",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    marginTop: "4px",
  };

  const completionStats = () => {
    const isCoopOrCondop = property?.property_type?.toLowerCase() === "coop" ||
                           property?.property_type?.toLowerCase() === "condop";

    const baseItems = [
      checklist.taxes_confirmed,
      checklist.sqft_confirmed,
      !!checklist.floorplan_status, // Count if status is set (available or not_available)
      !!checklist.rent_prediction_mode_low && !!checklist.rent_prediction_mode_high, // Count if both rent prediction modes are selected
      !!checklist.commission_percentage,
      !!checklist.tenant_occupied, // Count if tenant occupied is set (yes or no)
      checklist.broker_confirmed, // Count if confirmed with broker
      !!checklist.rental_application_available, // Count if rental app status is set
      checklist.rental_fees_confirmed, // Count if rental fees confirmed
      !!checklist.local_law_11_status, // Count if status is set (yes or no)
      !!checklist.property_video_status, // Count if status is set (available or not_available)
      // Note: other_costs_confirmed is optional and not counted (like discount)
    ];

    // Only count coop special notes if property is coop/condop
    const coopItems = isCoopOrCondop ? [
      !!checklist.coop_special_notes, // Count if filled (optional but counts toward completion)
    ] : [];

    const allItems = [...baseItems, ...coopItems];
    const completed = allItems.filter(Boolean).length;
    const totalItems = allItems.length;

    return { completed, total: totalItems };
  };

  const stats = completionStats();
  const progressPercentage = (stats.completed / stats.total) * 100;

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          Loading checklist...
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Property Checklist</span>
          {saveStatus === "saving" && <span style={{ fontSize: "11px", color: "#1976d2" }}>Saving...</span>}
          {saveStatus === "saved" && <span style={{ fontSize: "11px", color: "#2e7d32" }}>✓ Saved</span>}
          {saveStatus === "error" && <span style={{ fontSize: "11px", color: "#d32f2f" }}>Error saving</span>}
        </div>
        <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
          {stats.completed} of {stats.total} items completed
        </div>
        <div
          style={{
            width: "100%",
            height: "6px",
            backgroundColor: "#e0e0e0",
            borderRadius: "3px",
            marginTop: "8px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progressPercentage}%`,
              height: "100%",
              backgroundColor: "#2e7d32",
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>

      {/* Status Info Banner for MIS/Retired */}
      {statusInfo && (currentStatus === 'mis' || currentStatus === 'retired') && (
        <div style={{
          backgroundColor: currentStatus === 'mis' ? '#e8f5e9' : '#fff3e0',
          border: `2px solid ${currentStatus === 'mis' ? '#4caf50' : '#ff9800'}`,
          borderRadius: "6px",
          padding: "12px",
          marginBottom: "16px"
        }}>
          <div style={{ fontSize: "14px", fontWeight: "700", color: "#333", marginBottom: "8px" }}>
            Status: {currentStatus === 'mis' ? 'MIS' : 'Retired'}
          </div>
          <div style={{ fontSize: "13px", color: "#666", marginBottom: "4px" }}>
            <strong>Reason:</strong> {statusInfo.reason}
          </div>
          <div style={{ fontSize: "12px", color: "#999" }}>
            Changed by {statusInfo.changed_by?.first_name} {statusInfo.changed_by?.last_name} on{' '}
            {new Date(statusInfo.changed_at).toLocaleString()}
          </div>
        </div>
      )}

      {/* Co-op/Condop Specific Fields - Show FIRST if property is coop or condop */}
      {(property?.property_type?.toLowerCase() === "coop" ||
        property?.property_type?.toLowerCase() === "condop") && (
        <>
          <div style={{
            backgroundColor: "#fff3e0",
            padding: "12px",
            borderRadius: "6px",
            marginBottom: "16px",
            border: "2px solid #ff9800"
          }}>
            <div style={{
              fontSize: "16px",
              fontWeight: "700",
              color: "#e65100",
              marginBottom: "12px"
            }}>
              Co-op/Condop Requirements
            </div>

            {/* Sponsor Shares */}
            <div style={{...sectionStyle, borderBottom: "1px solid #ffcc80"}}>
              <div style={checkboxRowStyle}>
                <input
                  type="checkbox"
                  checked={!!checklist.coop_sponsor_shares}
                  onChange={() => handleCheckboxChange("coop_sponsor_shares")}
                />
                <label style={{ ...labelStyle, marginBottom: 0 }}>
                  Sponsor Shares
                </label>
              </div>
            </div>

            {/* Sublet Policy */}
            <div style={{...sectionStyle, borderBottom: "1px solid #ffcc80"}}>
              <label style={labelStyle}>Sublet Policy</label>
              <select
                value={checklist.coop_sublet_policy}
                onChange={(e) => handleInputChange("coop_sublet_policy", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select...</option>
                <option value="not_allowed">Not Allowed</option>
                <option value="allowed">Allowed</option>
                <option value="vesting_period">Vesting Period</option>
                <option value="sometimes">Sometimes</option>
              </select>

              {/* Vesting Period Details - shown when "Vesting Period" is selected */}
              {checklist.coop_sublet_policy === "vesting_period" && (
                <input
                  type="text"
                  placeholder="Enter vesting period details (e.g., 2 years)"
                  value={checklist.coop_sublet_vesting_details}
                  onChange={(e) => handleInputChange("coop_sublet_vesting_details", e.target.value)}
                  style={{ ...inputStyle, marginTop: "8px" }}
                />
              )}

              {/* Sometimes Details - shown when "Sometimes" is selected */}
              {checklist.coop_sublet_policy === "sometimes" && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                  <input
                    type="text"
                    placeholder="X"
                    value={checklist.coop_sublet_sometimes_numerator}
                    onChange={(e) => handleInputChange("coop_sublet_sometimes_numerator", e.target.value)}
                    style={{ ...inputStyle, flex: 1, margin: 0 }}
                  />
                  <span style={{ fontSize: "14px", color: "#666" }}>out of</span>
                  <input
                    type="text"
                    placeholder="Y"
                    value={checklist.coop_sublet_sometimes_denominator}
                    onChange={(e) => handleInputChange("coop_sublet_sometimes_denominator", e.target.value)}
                    style={{ ...inputStyle, flex: 1, margin: 0 }}
                  />
                </div>
              )}
            </div>

            {/* Allow International Buyers */}
            <div style={{...sectionStyle, borderBottom: "1px solid #ffcc80"}}>
              <label style={labelStyle}>Allow International Buyers</label>
              <select
                value={checklist.coop_international_buyers}
                onChange={(e) => handleInputChange("coop_international_buyers", e.target.value)}
                style={inputStyle}
              >
                <option value="">N/A</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            {/* Allow LLC Buyer */}
            <div style={{...sectionStyle, borderBottom: "1px solid #ffcc80"}}>
              <label style={labelStyle}>Allow LLC Buyer</label>
              <select
                value={checklist.coop_llc_buyer}
                onChange={(e) => handleInputChange("coop_llc_buyer", e.target.value)}
                style={inputStyle}
              >
                <option value="">N/A</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            {/* Requires Board Interview for Rentals */}
            <div style={{...sectionStyle, borderBottom: "1px solid #ffcc80"}}>
              <label style={labelStyle}>Requires Board Interview for Rentals</label>
              <select
                value={checklist.coop_board_interview_rentals}
                onChange={(e) => handleInputChange("coop_board_interview_rentals", e.target.value)}
                style={inputStyle}
              >
                <option value="">N/A</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            {/* Requires Board Interview for Sales */}
            <div style={{...sectionStyle, borderBottom: "none"}}>
              <label style={labelStyle}>Requires Board Interview for Sales</label>
              <select
                value={checklist.coop_board_interview_sales}
                onChange={(e) => handleInputChange("coop_board_interview_sales", e.target.value)}
                style={inputStyle}
              >
                <option value="">N/A</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
        </>
      )}

      {/* Property Details Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px",
        paddingBottom: "8px",
        borderBottom: "2px solid #000"
      }}>
        <span style={{ fontSize: "18px", fontWeight: "700", color: "#333" }}>
          Property Details
        </span>
        {property?.property_type && (
          <span style={{ fontSize: "14px", fontWeight: "600", color: "#666" }}>
            {property.property_type}
          </span>
        )}
      </div>

      {/* Summary Section - Three Separate Boxes */}
      <div style={sectionStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>
            AI Summary
          </label>
          {generatingSummary && (
            <div style={{
              width: "16px",
              height: "16px",
              border: "2px solid #e0e0e0",
              borderTop: "2px solid #1976d2",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite"
            }} />
          )}
        </div>

        {/* Unit Info */}
        <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginTop: "8px", marginBottom: "4px" }}>
          Unit Info
        </label>
        <textarea
          placeholder="AI will extract unit-specific details (layout, features, upgrades, storage, etc.)"
          value={checklist.ai_summary_unit}
          onChange={(e) => handleInputChange("ai_summary_unit", e.target.value)}
          style={{
            ...textareaStyle,
            minHeight: "70px"
          }}
        />

        {/* Building Info */}
        <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginTop: "12px", marginBottom: "4px" }}>
          Building Info
        </label>
        <textarea
          placeholder="AI will extract building details (amenities, services, doorman, gym, etc.)"
          value={checklist.ai_summary_building}
          onChange={(e) => handleInputChange("ai_summary_building", e.target.value)}
          style={{
            ...textareaStyle,
            minHeight: "70px"
          }}
        />

        {/* Building Location Info */}
        <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginTop: "12px", marginBottom: "4px" }}>
          Building Location Info
        </label>
        <textarea
          placeholder="AI will extract location details (neighborhood, transit, nearby attractions, etc.)"
          value={checklist.ai_summary_location}
          onChange={(e) => handleInputChange("ai_summary_location", e.target.value)}
          style={{
            ...textareaStyle,
            minHeight: "70px"
          }}
        />

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>

      {/* Taxes - Hidden for co-ops and condops */}
      {property?.property_type?.toLowerCase() !== 'coop' && property?.property_type?.toLowerCase() !== 'condop' && (
        <div style={sectionStyle}>
          {property?.listing_details?.tax_bill?.status === "no_data_verified" && (
            <div style={{
              backgroundColor: "#ffebee",
              color: "#d32f2f",
              padding: "8px 12px",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: "600",
              marginBottom: "12px",
              border: "1px solid #ef9a9a",
            }}>
              No tax data available on NYC DOF — verified by DQ team
            </div>
          )}
          <div style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={checklist.taxes_confirmed}
              onChange={() => handleCheckboxChange("taxes_confirmed")}
            />
            <label style={{ ...labelStyle, marginBottom: 0 }}>
              Taxes Confirmed
            </label>
          </div>
          <CurrencyInput
            allowDecimals
            placeholder="Monthly tax amount"
            value={checklist.taxes_amount}
            onChange={(v) => handleInputChange("taxes_amount", v)}
            style={inputStyle}
          />

          {/* Tax Abatement Checkbox */}
          <div style={{ ...checkboxRowStyle, marginTop: "12px" }}>
            <input
              type="checkbox"
              checked={!!checklist.taxes_abatement_enabled}
              onChange={(e) => handleInputChange("taxes_abatement_enabled", e.target.checked)}
            />
            <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginBottom: 0 }}>
              Tax abatement
            </label>
          </div>

          {/* Tax Abatement Details (only shown when checkbox is checked) */}
          {checklist.taxes_abatement_enabled && (
            <div style={{ marginTop: "4px", display: "flex", flexDirection: "column", gap: "6px" }}>
              <select
                value={checklist.taxes_abatement_type || ""}
                onChange={(e) => handleInputChange("taxes_abatement_type", e.target.value)}
                style={inputStyle}
              >
                <option value="">-- Select Type --</option>
                <option value="421a">421a</option>
                <option value="j-51">J-51</option>
                <option value="condo_abatement">Condo Abatement</option>
                <option value="other">Other</option>
              </select>
              {checklist.taxes_abatement_type === "other" && (
                <input
                  type="text"
                  placeholder="Other abatement type..."
                  value={checklist.taxes_abatement_type_other || ""}
                  onChange={(e) => handleInputChange("taxes_abatement_type_other", e.target.value)}
                  style={inputStyle}
                />
              )}
              <div>
                <label style={{ fontSize: "11px", color: "#666" }}>Expires (Year)</label>
                <input
                  type="number"
                  value={checklist.taxes_abatement_date ? checklist.taxes_abatement_date.slice(0, 4) : ""}
                  onChange={(e) => handleInputChange("taxes_abatement_date", e.target.value)}
                  placeholder="e.g. 2034"
                  min="2020"
                  max="2060"
                  style={inputStyle}
                />
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <CurrencyInput
                  allowDecimals
                  placeholder="Taxes w/ abatement ($/mo)"
                  value={checklist.taxes_abatement_with || ""}
                  onChange={(v) => handleInputChange("taxes_abatement_with", v)}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <CurrencyInput
                  allowDecimals
                  placeholder="Taxes w/o abatement ($/mo)"
                  value={checklist.taxes_abatement_without || ""}
                  onChange={(v) => handleInputChange("taxes_abatement_without", v)}
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            </div>
          )}
          <input
            type="text"
            placeholder="ACRIS link"
            value={checklist.taxes_acris_link}
            onChange={(e) =>
              handleInputChange("taxes_acris_link", e.target.value)
            }
            style={inputStyle}
          />
          {checklist.taxes_acris_link && (
            <button
              style={linkButtonStyle}
              onClick={() => window.open(checklist.taxes_acris_link, "_blank")}
            >
              Open ACRIS
            </button>
          )}
        </div>
      )}

      {/* Square Footage */}
      <div style={sectionStyle}>
        <div style={checkboxRowStyle}>
          <input
            type="checkbox"
            checked={checklist.sqft_confirmed}
            onChange={() => handleCheckboxChange("sqft_confirmed")}
          />
          <label style={{ ...labelStyle, marginBottom: 0 }}>
            Square Footage Confirmed
          </label>
        </div>

        {/* Interior Area */}
        <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginTop: "8px", marginBottom: "4px" }}>
          Interior area
        </label>
        <input
          type="text"
          placeholder="Interior square footage"
          value={checklist.sqft_amount}
          onChange={(e) => {
            const value = e.target.value;
            const interior = parseFloat(value) || 0;

            // Calculate composite based on whether exterior is enabled
            let composite;
            if (checklist.sqft_exterior_enabled && checklist.sqft_exterior_amount) {
              const exterior = parseFloat(checklist.sqft_exterior_amount) || 0;
              composite = interior > 0 ? (interior + (exterior / 3)).toFixed(0) : "";
            } else {
              composite = interior > 0 ? interior.toFixed(0) : "";
            }

            // Update both fields at once
            setChecklist({
              ...checklist,
              sqft_amount: value,
              sqft_composite: composite
            });
          }}
          style={inputStyle}
        />

        {/* Exterior Area Checkbox */}
        <div style={{ ...checkboxRowStyle, marginTop: "12px" }}>
          <input
            type="checkbox"
            checked={!!checklist.sqft_exterior_enabled}
            onChange={(e) => {
              const isChecked = e.target.checked;
              const interior = parseFloat(checklist.sqft_amount) || 0;

              if (!isChecked) {
                // When unchecking: disable exterior, clear exterior amount, recalculate composite
                setChecklist({
                  ...checklist,
                  sqft_exterior_enabled: false,
                  sqft_exterior_amount: "",
                  sqft_composite: interior > 0 ? interior.toFixed(0) : ""
                });
              } else {
                // When checking: just enable exterior
                setChecklist({
                  ...checklist,
                  sqft_exterior_enabled: true
                });
              }
            }}
          />
          <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginBottom: 0 }}>
            Exterior area
          </label>
        </div>

        {/* Exterior Area Input (only shown when checkbox is checked) */}
        {checklist.sqft_exterior_enabled && (
          <input
            type="text"
            placeholder="Exterior square footage"
            value={checklist.sqft_exterior_amount}
            onChange={(e) => {
              const value = e.target.value;
              const interior = parseFloat(checklist.sqft_amount) || 0;
              const exterior = parseFloat(value) || 0;
              const composite = interior + (exterior / 3);

              // Update both fields at once
              setChecklist({
                ...checklist,
                sqft_exterior_amount: value,
                sqft_composite: composite > 0 ? composite.toFixed(0) : ""
              });
            }}
            style={{ ...inputStyle, marginTop: "4px" }}
          />
        )}

        {/* Composite SqFt */}
        <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginTop: "12px", marginBottom: "4px" }}>
          Composite sqft
        </label>
        <input
          type="text"
          placeholder="Calculated composite sqft"
          value={checklist.sqft_composite}
          readOnly
          style={{ ...inputStyle, backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
        />

        {/* Price per sqft - calculated using composite sqft */}
        {property?.price && checklist.sqft_composite && parseFloat(checklist.sqft_composite) > 0 && (
          <div style={{ marginTop: "12px", fontSize: "13px", color: "#666" }}>
            <strong>Price per sqft:</strong> ${Math.round(property.price / parseFloat(checklist.sqft_composite)).toLocaleString()}
          </div>
        )}
      </div>

      {/* Convertible Bedrooms */}
      <div style={sectionStyle}>
        <div style={checkboxRowStyle}>
          <input
            type="checkbox"
            checked={checklist.convertible_enabled}
            onChange={(e) => {
              if (e.target.checked) {
                handleInputChange("convertible_enabled", true);
              } else {
                setChecklist(prev => ({ ...prev, convertible_enabled: false, convertible_bedrooms: "" }));
              }
            }}
          />
          <label style={{ fontWeight: "600", fontSize: "14px" }}>Convertible to</label>
          {checklist.convertible_enabled && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "8px" }}>
              <input
                type="number"
                min="1"
                max="10"
                placeholder="#"
                value={checklist.convertible_bedrooms}
                onChange={(e) => handleInputChange("convertible_bedrooms", e.target.value)}
                style={{ ...inputStyle, width: "60px", margin: 0 }}
              />
              <span style={{ fontSize: "13px", color: "#666" }}>bedrooms</span>
            </div>
          )}
        </div>
        {checklist.convertible_enabled && checklist.convertible_bedrooms && (
          <div style={{ fontSize: "12px", color: "#856404", backgroundColor: "#fff3cd", padding: "8px 12px", borderRadius: "4px", marginTop: "8px" }}>
            Listed as {property?.bedrooms || "?"}BR but convertible to {checklist.convertible_bedrooms}BR.
            Use "Re-run AI" to generate comps with {checklist.convertible_bedrooms}BR.
          </div>
        )}
      </div>

      {/* Floor Plan */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Floor Plan</label>
        <select
          value={checklist.floorplan_status}
          onChange={(e) => handleInputChange("floorplan_status", e.target.value)}
          style={inputStyle}
        >
          <option value="">Not Verified</option>
          <option value="available">Available</option>
          <option value="not_available">Not Available</option>
        </select>
        <input
          type="text"
          placeholder="Floor plan link (if available)"
          value={checklist.floorplan_link}
          onChange={(e) => handleInputChange("floorplan_link", e.target.value)}
          style={inputStyle}
        />
        {checklist.floorplan_link && (
          <button
            style={linkButtonStyle}
            onClick={() => window.open(checklist.floorplan_link, "_blank")}
          >
            View Floor Plan
          </button>
        )}
      </div>

      {/* Tenant Occupied - MOVED ABOVE RENT PREDICTION */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Tenant Occupied?</label>
        <div style={{ display: "flex", gap: "16px", marginBottom: "8px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", cursor: "pointer" }}>
            <input
              type="radio"
              name="tenant_occupied"
              checked={checklist.tenant_occupied === "yes"}
              onChange={() => handleInputChange("tenant_occupied", "yes")}
            />
            Yes
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", cursor: "pointer" }}>
            <input
              type="radio"
              name="tenant_occupied"
              checked={checklist.tenant_occupied === "no"}
              onChange={() => handleInputChange("tenant_occupied", "no")}
            />
            No
          </label>
        </div>

        {/* Show fields when tenant occupied = Yes */}
        {checklist.tenant_occupied === "yes" && (
          <>
            <CurrencyInput
              placeholder="Current monthly rent"
              value={checklist.tenant_current_rent}
              onChange={(v) => handleInputChange("tenant_current_rent", v)}
              style={inputStyle}
            />

            {/* Lease Type Section */}
            <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginTop: "12px" }}>
              Lease Type
            </label>
            <div style={{ display: "flex", gap: "16px", marginBottom: "8px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="tenant_lease_type"
                  checked={checklist.tenant_lease_type === "month_to_month"}
                  onChange={() => handleInputChange("tenant_lease_type", "month_to_month")}
                />
                Month to Month
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="tenant_lease_type"
                  checked={checklist.tenant_lease_type === "fixed_term"}
                  onChange={() => handleInputChange("tenant_lease_type", "fixed_term")}
                />
                Fixed Term
              </label>
            </div>

            {/* Show lease details if fixed term selected */}
            {checklist.tenant_lease_type === "fixed_term" && (
              <>
                <label style={{ ...labelStyle, fontSize: "12px", color: "#666" }}>
                  Lease End Date
                </label>
                <input
                  type="date"
                  value={checklist.tenant_lease_end_date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val && val < new Date().toISOString().split('T')[0]) return;
                    handleInputChange("tenant_lease_end_date", val);
                  }}
                  style={{ ...inputStyle, maxWidth: "250px" }}
                />

                {/* Multi-period rent schedule */}
                <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginTop: "12px" }}>
                  Rent Schedule (if rent changes during lease)
                </label>
                {(checklist.tenant_rent_periods || []).map((period, idx) => (
                  <div key={idx} style={{ display: "flex", gap: "6px", marginBottom: "6px", alignItems: "center" }}>
                    <CurrencyInput placeholder="Rent" value={period.rent}
                      onChange={(v) => {
                        const periods = [...(checklist.tenant_rent_periods || [])];
                        periods[idx] = { ...periods[idx], rent: v };
                        handleInputChange("tenant_rent_periods", periods);
                      }}
                      style={{ ...inputStyle, width: "90px", marginBottom: 0 }} />
                    <span style={{ fontSize: 12, color: "#666" }}>from</span>
                    <input type="date" value={period.from || ""}
                      onChange={(e) => {
                        const periods = [...(checklist.tenant_rent_periods || [])];
                        periods[idx] = { ...periods[idx], from: e.target.value };
                        handleInputChange("tenant_rent_periods", periods);
                      }}
                      style={{ ...inputStyle, width: "130px", marginBottom: 0 }} />
                    <span style={{ fontSize: 12, color: "#666" }}>to</span>
                    <input type="date" value={period.to || ""}
                      onChange={(e) => {
                        const periods = [...(checklist.tenant_rent_periods || [])];
                        periods[idx] = { ...periods[idx], to: e.target.value };
                        handleInputChange("tenant_rent_periods", periods);
                      }}
                      style={{ ...inputStyle, width: "130px", marginBottom: 0 }} />
                    <span onClick={() => {
                      const periods = (checklist.tenant_rent_periods || []).filter((_, i) => i !== idx);
                      handleInputChange("tenant_rent_periods", periods);
                    }} style={{ cursor: "pointer", color: "#d32f2f", fontSize: 16 }}>&times;</span>
                  </div>
                ))}
                <button type="button" onClick={() => {
                  const periods = [...(checklist.tenant_rent_periods || []), { rent: "", from: "", to: "" }];
                  handleInputChange("tenant_rent_periods", periods);
                }} style={{ fontSize: 12, padding: "4px 10px", border: "1px solid #ccc", borderRadius: 4, background: "#f5f5f5", cursor: "pointer" }}>
                  + Add rent period
                </button>
              </>
            )}
          </>
        )}

        {/* Show occupancy notes when tenant occupied = No */}
        {checklist.tenant_occupied === "no" && (
          <>
            <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginTop: "8px" }}>
              Occupancy Notes
            </label>
            <textarea
              placeholder="Notes about occupancy status, move-in readiness, etc."
              value={checklist.occupancy_notes}
              onChange={(e) => handleInputChange("occupancy_notes", e.target.value)}
              style={textareaStyle}
            />
          </>
        )}
      </div>

      {/* Rent Prediction */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Rent Prediction</label>

        <div style={{ display: "flex", gap: "12px" }}>
          {/* Low Range */}
          <div style={{ flex: 1 }}>
            <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginBottom: "6px" }}>Minimum</label>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "8px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="rent_prediction_mode_low"
                  checked={checklist.rent_prediction_mode_low === "ml"}
                  onChange={() => handleRentPredictionModeChange("rent_prediction_mode_low", "ml")}
                />
                ML {prediction?.predicted_rent && `($${prediction.predicted_rent.toLocaleString()})`}
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: (aiPrediction?.predicted_rent || agentAnalysis?.agent_adjusted_rent) ? "pointer" : "not-allowed", opacity: (aiPrediction?.predicted_rent || agentAnalysis?.agent_adjusted_rent) ? 1 : 0.5 }}>
                <input
                  type="radio"
                  name="rent_prediction_mode_low"
                  checked={checklist.rent_prediction_mode_low === "ai"}
                  onChange={() => handleRentPredictionModeChange("rent_prediction_mode_low", "ai")}
                  disabled={!aiPrediction?.predicted_rent && !agentAnalysis?.agent_adjusted_rent}
                />
                AI {(aiPrediction?.predicted_rent || agentAnalysis?.agent_adjusted_rent) && `($${(aiPrediction?.predicted_rent || agentAnalysis?.agent_adjusted_rent).toLocaleString()})`}
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: (checklist.tenant_occupied === "yes" && checklist.tenant_current_rent) ? "pointer" : "not-allowed", opacity: (checklist.tenant_occupied === "yes" && checklist.tenant_current_rent) ? 1 : 0.5 }}>
                <input
                  type="radio"
                  name="rent_prediction_mode_low"
                  checked={checklist.rent_prediction_mode_low === "current_tenant"}
                  onChange={() => handleRentPredictionModeChange("rent_prediction_mode_low", "current_tenant")}
                  disabled={checklist.tenant_occupied !== "yes" || !checklist.tenant_current_rent}
                />
                Tenant {checklist.tenant_current_rent && checklist.tenant_occupied === "yes" && `($${parseFloat(checklist.tenant_current_rent).toLocaleString()})`}
              </label>

              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="rent_prediction_mode_low"
                    checked={checklist.rent_prediction_mode_low === "custom"}
                    onChange={() => handleRentPredictionModeChange("rent_prediction_mode_low", "custom")}
                  />
                  Custom
                </label>
                {checklist.rent_prediction_mode_low === "custom" && (
                  <CurrencyInput
                    placeholder="Min"
                    value={checklist.rent_prediction_range_low}
                    onChange={(v) => handleInputChange("rent_prediction_range_low", v)}
                    style={{
                      ...inputStyle,
                      fontSize: "12px",
                      padding: "6px",
                      width: "100px",
                      marginLeft: "4px"
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* High Range */}
          <div style={{ flex: 1 }}>
            <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginBottom: "6px" }}>Maximum</label>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "8px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="rent_prediction_mode_high"
                  checked={checklist.rent_prediction_mode_high === "ml"}
                  onChange={() => handleRentPredictionModeChange("rent_prediction_mode_high", "ml")}
                />
                ML {prediction?.predicted_rent && `($${prediction.predicted_rent.toLocaleString()})`}
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: (aiPrediction?.predicted_rent || agentAnalysis?.agent_adjusted_rent) ? "pointer" : "not-allowed", opacity: (aiPrediction?.predicted_rent || agentAnalysis?.agent_adjusted_rent) ? 1 : 0.5 }}>
                <input
                  type="radio"
                  name="rent_prediction_mode_high"
                  checked={checklist.rent_prediction_mode_high === "ai"}
                  onChange={() => handleRentPredictionModeChange("rent_prediction_mode_high", "ai")}
                  disabled={!aiPrediction?.predicted_rent && !agentAnalysis?.agent_adjusted_rent}
                />
                AI {(aiPrediction?.predicted_rent || agentAnalysis?.agent_adjusted_rent) && `($${(aiPrediction?.predicted_rent || agentAnalysis?.agent_adjusted_rent).toLocaleString()})`}
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: (checklist.tenant_occupied === "yes" && checklist.tenant_current_rent) ? "pointer" : "not-allowed", opacity: (checklist.tenant_occupied === "yes" && checklist.tenant_current_rent) ? 1 : 0.5 }}>
                <input
                  type="radio"
                  name="rent_prediction_mode_high"
                  checked={checklist.rent_prediction_mode_high === "current_tenant"}
                  onChange={() => handleRentPredictionModeChange("rent_prediction_mode_high", "current_tenant")}
                  disabled={checklist.tenant_occupied !== "yes" || !checklist.tenant_current_rent}
                />
                Tenant {checklist.tenant_current_rent && checklist.tenant_occupied === "yes" && `($${parseFloat(checklist.tenant_current_rent).toLocaleString()})`}
              </label>

              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="rent_prediction_mode_high"
                    checked={checklist.rent_prediction_mode_high === "custom"}
                    onChange={() => handleRentPredictionModeChange("rent_prediction_mode_high", "custom")}
                  />
                  Custom
                </label>
                {checklist.rent_prediction_mode_high === "custom" && (
                  <CurrencyInput
                    placeholder="Max"
                    value={checklist.rent_prediction_range_high}
                    onChange={(v) => handleInputChange("rent_prediction_range_high", v)}
                    style={{
                      ...inputStyle,
                      fontSize: "12px",
                      padding: "6px",
                      width: "100px",
                      marginLeft: "4px"
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Commission */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Commission %</label>
        <input
          type="text"
          placeholder="e.g., 15%"
          value={checklist.commission_percentage}
          onChange={(e) =>
            handleInputChange("commission_percentage", e.target.value)
          }
          style={inputStyle}
        />
      </div>

      {/* Discount */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Discount %</label>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <input
            type="number"
            placeholder="0"
            value={checklist.discount_pct}
            onChange={(e) => handleInputChange("discount_pct", e.target.value)}
            style={{ ...inputStyle, width: "100px" }}
            step="0.1"
            min="0"
            max="100"
          />
          {property?.asking_price && checklist.discount_pct && parseFloat(checklist.discount_pct) > 0 && (
            <div style={{ fontSize: "12px", color: "#666" }}>
              Adjusted: ${(property.asking_price * (1 - parseFloat(checklist.discount_pct) / 100)).toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {/* Broker Notes */}
      <div style={sectionStyle}>
        <div style={checkboxRowStyle}>
          <input
            type="checkbox"
            checked={checklist.broker_confirmed}
            onChange={() => handleCheckboxChange("broker_confirmed")}
          />
          <label style={{ ...labelStyle, marginBottom: 0 }}>
            Confirmed with Broker
          </label>
        </div>
        <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginTop: "8px" }}>
          General Notes
        </label>
        <textarea
          placeholder="Any general broker notes..."
          value={checklist.broker_notes}
          onChange={(e) => handleInputChange("broker_notes", e.target.value)}
          style={textareaStyle}
        />
        <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginTop: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
          <input
            type="checkbox"
            checked={checklist.assessment_enabled}
            onChange={(e) => handleInputChange("assessment_enabled", e.target.checked)}
          />
          Assessment
        </label>
        {checklist.assessment_enabled && (() => {
          const monthlyCost = checklist.assessment_cost ? parseInt(checklist.assessment_cost) : 0;
          const hasEndDate = checklist.assessment_type === "monthly" && checklist.assessment_end_date;
          const remainingMonths = hasEndDate ? Math.max(0, Math.ceil((new Date(checklist.assessment_end_date) - new Date()) / (1000 * 60 * 60 * 24 * 30.44))) : 0;
          const totalCost = hasEndDate ? monthlyCost * remainingMonths : 0;
          const creditPct = checklist.assessment_credit_pct ? parseFloat(checklist.assessment_credit_pct) : 0;
          const effectiveTotal = totalCost * (1 - creditPct / 100);

          return (
          <div style={{ marginLeft: "4px", marginBottom: "8px" }}>
            <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
              <CurrencyInput
                placeholder="$/month"
                value={checklist.assessment_cost}
                onChange={(v) => handleInputChange("assessment_cost", v)}
                style={{ ...inputStyle, maxWidth: "120px" }}
              />
              <select
                value={checklist.assessment_type}
                onChange={(e) => handleInputChange("assessment_type", e.target.value)}
                style={{ ...inputStyle, maxWidth: "220px" }}
              >
                <option value="">Select type...</option>
                <option value="monthly">With end date (closing costs)</option>
                <option value="forever">Ongoing / forever (carries)</option>
              </select>
            </div>
            {checklist.assessment_type === "monthly" && (
              <div style={{ display: "flex", gap: "8px", marginBottom: "8px", alignItems: "center" }}>
                <div>
                  <label style={{ fontSize: "11px", color: "#888" }}>End Date</label>
                  <input
                    type="date"
                    value={checklist.assessment_end_date}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val && val < new Date().toISOString().split('T')[0]) return;
                      handleInputChange("assessment_end_date", val);
                    }}
                    style={{ ...inputStyle, maxWidth: "180px" }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "11px", color: "#888" }}>Credit %</label>
                  <input
                    type="text"
                    placeholder="0"
                    value={checklist.assessment_credit_pct || ""}
                    onChange={(e) => {
                      const num = e.target.value.replace(/[^\d]/g, '');
                      handleInputChange("assessment_credit_pct", num);
                    }}
                    style={{ ...inputStyle, maxWidth: "75px" }}
                  />
                </div>
              </div>
            )}
            <textarea
              placeholder="Assessment details/notes..."
              value={checklist.broker_assessments}
              onChange={(e) => handleInputChange("broker_assessments", e.target.value)}
              style={{ ...textareaStyle, minHeight: "40px" }}
            />
            {checklist.assessment_type && monthlyCost > 0 && (
              <div style={{ fontSize: "11px", color: "#1565c0", marginTop: "4px", padding: "6px 8px", background: "#e3f2fd", borderRadius: "4px" }}>
                {checklist.assessment_type === "monthly" && hasEndDate ? (
                  <>
                    ${monthlyCost.toLocaleString()}/mo × {remainingMonths} months = ${totalCost.toLocaleString()}
                    {creditPct > 0 && (
                      <> | Credit {creditPct}% = <strong>${Math.round(effectiveTotal).toLocaleString()}</strong> added to closing costs</>
                    )}
                    {!creditPct && (
                      <> → <strong>${totalCost.toLocaleString()}</strong> added to closing costs</>
                    )}
                  </>
                ) : checklist.assessment_type === "monthly" ? (
                  `$${monthlyCost.toLocaleString()}/mo — select end date to calculate total`
                ) : (
                  `$${monthlyCost.toLocaleString()}/mo added to carrying costs (affects cap rate)`
                )}
              </div>
            )}
          </div>
          );
        })()}
        <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginTop: "8px" }}>
          Building Financial Issues?
        </label>
        <textarea
          placeholder="Any building financial issues (if any)..."
          value={checklist.broker_building_financials}
          onChange={(e) => handleInputChange("broker_building_financials", e.target.value)}
          style={textareaStyle}
        />
        <label style={{ ...labelStyle, fontSize: "12px", color: "#666", marginTop: "8px" }}>
          Negotiability
        </label>
        <textarea
          placeholder="Is there room to negotiate the asking price? Any insights on reducing the price..."
          value={checklist.broker_negotiability}
          onChange={(e) => handleInputChange("broker_negotiability", e.target.value)}
          style={textareaStyle}
        />
      </div>

      {/* Rental Application */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Rental Online Application?</label>
        <div style={{ display: "flex", gap: "16px", marginBottom: "8px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", cursor: "pointer" }}>
            <input
              type="radio"
              name="rental_application"
              checked={checklist.rental_application_available === "yes"}
              onChange={() => handleInputChange("rental_application_available", "yes")}
            />
            Yes
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", cursor: "pointer" }}>
            <input
              type="radio"
              name="rental_application"
              checked={checklist.rental_application_available === "no"}
              onChange={() => handleInputChange("rental_application_available", "no")}
            />
            No
          </label>
        </div>
        {checklist.rental_application_available === "yes" && (
          <>
            <input
              type="text"
              placeholder="Application link"
              value={checklist.rental_application_link}
              onChange={(e) =>
                handleInputChange("rental_application_link", e.target.value)
              }
              style={inputStyle}
            />
            {checklist.rental_application_link && (
              <button
                style={linkButtonStyle}
                onClick={() =>
                  window.open(checklist.rental_application_link, "_blank")
                }
              >
                Open Application
              </button>
            )}
          </>
        )}
      </div>

      {/* Rental Fees */}
      <div style={sectionStyle}>
        <div style={checkboxRowStyle}>
          <input
            type="checkbox"
            checked={checklist.rental_fees_confirmed}
            onChange={() => handleCheckboxChange("rental_fees_confirmed")}
          />
          <label style={{ ...labelStyle, marginBottom: 0 }}>
            Rental Fees Confirmed
          </label>
        </div>
        <textarea
          placeholder="Application fees, move-in fees, etc. (if any)..."
          value={checklist.rental_fees}
          onChange={(e) => handleInputChange("rental_fees", e.target.value)}
          style={{...textareaStyle, marginTop: "8px"}}
        />
      </div>

      {/* Local Law 11 */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Local Law 11</label>
        <select
          value={checklist.local_law_11_status}
          onChange={(e) => handleInputChange("local_law_11_status", e.target.value)}
          style={inputStyle}
        >
          <option value="">Not Verified</option>
          <option value="yes">Yes - Applicable</option>
          <option value="no">No - Not Applicable</option>
        </select>
        {checklist.local_law_11_status === "yes" && (
          <>
            <input
              type="date"
              placeholder="LL11 compliance date"
              value={checklist.local_law_11_date}
              onChange={(e) =>
                handleInputChange("local_law_11_date", e.target.value)
              }
              style={inputStyle}
            />
            <textarea
              placeholder="Notes about Local Law 11 (e.g., compliance status, upcoming work)"
              value={checklist.local_law_11_notes}
              onChange={(e) =>
                handleInputChange("local_law_11_notes", e.target.value)
              }
              style={textareaStyle}
            />
          </>
        )}
      </div>

      {/* Property Video */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Property Video</label>
        <select
          value={checklist.property_video_status}
          onChange={(e) => handleInputChange("property_video_status", e.target.value)}
          style={inputStyle}
        >
          <option value="">Not Verified</option>
          <option value="available">Available</option>
          <option value="not_available">Not Available</option>
        </select>
        <input
          type="text"
          placeholder="Video link (if available)"
          value={checklist.property_video_link}
          onChange={(e) =>
            handleInputChange("property_video_link", e.target.value)
          }
          style={inputStyle}
        />
        {checklist.property_video_link && (
          <button
            style={linkButtonStyle}
            onClick={() =>
              window.open(checklist.property_video_link, "_blank")
            }
          >
            Watch Video
          </button>
        )}
      </div>

      {/* Other Costs (Parking, Storage, etc.) */}
      <div style={sectionStyle}>
        <div style={checkboxRowStyle}>
          <input
            type="checkbox"
            checked={checklist.other_costs_confirmed}
            onChange={() => handleCheckboxChange("other_costs_confirmed")}
            style={{ cursor: "pointer" }}
          />
          <label style={{ ...labelStyle, marginBottom: 0 }}>
            Other Costs (Parking, Storage, etc.)
          </label>
        </div>

        {checklist.other_costs_confirmed && (
          <div style={{ marginTop: "15px" }}>
            {/* List of other cost items */}
            {(checklist.other_costs_items || []).map((item) => (
              <div
                key={item.id}
                style={{
                  marginBottom: "12px",
                  padding: "12px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "6px"
                }}
              >
                {/* First row: Name, Cost, and Remove button */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.5fr 40px",
                  gap: "10px",
                  alignItems: "center",
                  marginBottom: "10px"
                }}>
                  {/* Name field */}
                  <input
                    type="text"
                    placeholder="e.g., Parking, Storage"
                    value={item.name}
                    onChange={(e) => updateOtherCostItem(item.id, "name", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />

                  {/* Cost field */}
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <span style={{ fontSize: "14px", color: "#666" }}>$</span>
                    <CurrencyInput
                      allowDecimals
                      placeholder="Cost"
                      value={item.cost}
                      onChange={(v) => updateOtherCostItem(item.id, "cost", v)}
                      style={{
                        padding: "8px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        fontSize: "14px",
                        width: "100%"
                      }}
                    />
                  </div>

                  {/* Remove button */}
                  <button
                    onClick={() => removeOtherCostItem(item.id)}
                    style={{
                      padding: "6px",
                      backgroundColor: "#f44336",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px"
                    }}
                    title="Remove item"
                  >
                    ×
                  </button>
                </div>

                {/* Second row: Payment type radio buttons */}
                <div style={{ display: "flex", gap: "20px", alignItems: "center", paddingLeft: "4px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", cursor: "pointer", margin: 0 }}>
                    <input
                      type="radio"
                      name={`payment_type_${item.id}`}
                      checked={item.payment_type === "included"}
                      onChange={() => updateOtherCostItem(item.id, "payment_type", "included")}
                      style={{ cursor: "pointer" }}
                    />
                    Included
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", cursor: "pointer", margin: 0 }}>
                    <input
                      type="radio"
                      name={`payment_type_${item.id}`}
                      checked={item.payment_type === "financed"}
                      onChange={() => updateOtherCostItem(item.id, "payment_type", "financed")}
                      style={{ cursor: "pointer" }}
                    />
                    Financed
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", cursor: "pointer", margin: 0 }}>
                    <input
                      type="radio"
                      name={`payment_type_${item.id}`}
                      checked={item.payment_type === "closing_costs"}
                      onChange={() => updateOtherCostItem(item.id, "payment_type", "closing_costs")}
                      style={{ cursor: "pointer" }}
                    />
                    Closing Costs
                  </label>
                </div>
              </div>
            ))}

            {/* Add item button */}
            <button
              onClick={addOtherCostItem}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4caf50",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                marginTop: "10px"
              }}
            >
              + Add Item
            </button>

            {/* Explanation text */}
            <div style={{
              marginTop: "15px",
              padding: "12px",
              backgroundColor: "#e3f2fd",
              borderRadius: "6px",
              fontSize: "13px",
              color: "#1565c0",
              lineHeight: "1.5"
            }}>
              <strong>Note:</strong> <em>Included</em> = no impact (already part of the deal).
              <em>Financed</em> = adds to asking price.
              <em>Closing Costs</em> = adds to out-of-pocket closing costs.
            </div>
          </div>
        )}
      </div>

      {/* Co-op Special Notes - Only show for coop/condop */}
      {(property?.property_type?.toLowerCase() === "coop" ||
        property?.property_type?.toLowerCase() === "condop") && (
        <div style={sectionStyle}>
          <label style={labelStyle}>Co-op Special Notes/Policies</label>
          <textarea
            placeholder="Special policies, board approval notes, etc."
            value={checklist.coop_special_notes}
            onChange={(e) =>
              handleInputChange("coop_special_notes", e.target.value)
            }
            style={textareaStyle}
          />
        </div>
      )}

      {/* Manual Status Change Buttons */}
      <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "2px solid #e0e0e0" }}>
        <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "12px", color: "#333" }}>
          Manual Status Override
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={() => openModal("mis")}
            disabled={!reviewId}
            style={{
              flex: 1,
              padding: "10px",
              fontSize: "13px",
              fontWeight: "600",
              backgroundColor: "#4caf50",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: reviewId ? "pointer" : "not-allowed",
              opacity: reviewId ? 1 : 0.5
            }}
            onMouseEnter={(e) => {
              if (reviewId) e.target.style.backgroundColor = "#45a049";
            }}
            onMouseLeave={(e) => {
              if (reviewId) e.target.style.backgroundColor = "#4caf50";
            }}
          >
            Mark as MIS
          </button>
          <button
            onClick={() => openModal("retired")}
            disabled={!reviewId}
            style={{
              flex: 1,
              padding: "10px",
              fontSize: "13px",
              fontWeight: "600",
              backgroundColor: "#ff9800",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: reviewId ? "pointer" : "not-allowed",
              opacity: reviewId ? 1 : 0.5
            }}
            onMouseEnter={(e) => {
              if (reviewId) e.target.style.backgroundColor = "#f57c00";
            }}
            onMouseLeave={(e) => {
              if (reviewId) e.target.style.backgroundColor = "#ff9800";
            }}
          >
            Mark as Retired
          </button>
        </div>
      </div>

      {/* Modal for MIS/Retired comment */}
      {showModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "24px",
            maxWidth: "500px",
            width: "90%",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
          }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", color: "#333" }}>
              {modalType === "mis" ? "Mark as MIS" : "Mark as Retired"}
            </h3>
            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#666" }}>
              Please provide a reason for this status change (required):
            </p>
            <textarea
              value={statusComment}
              onChange={(e) => setStatusComment(e.target.value)}
              placeholder="Enter reason..."
              style={{
                width: "100%",
                minHeight: "100px",
                padding: "12px",
                fontSize: "14px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontFamily: "inherit",
                resize: "vertical",
                boxSizing: "border-box"
              }}
              autoFocus
            />
            <div style={{ display: "flex", gap: "12px", marginTop: "20px", justifyContent: "flex-end" }}>
              <button
                onClick={closeModal}
                disabled={submitting}
                style={{
                  padding: "10px 20px",
                  fontSize: "14px",
                  backgroundColor: "#f5f5f5",
                  color: "#333",
                  border: "none",
                  borderRadius: "4px",
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleStatusChange(modalType)}
                disabled={submitting || !statusComment.trim()}
                style={{
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: "600",
                  backgroundColor: modalType === "mis" ? "#ff9800" : "#9e9e9e",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: (submitting || !statusComment.trim()) ? "not-allowed" : "pointer",
                  opacity: (submitting || !statusComment.trim()) ? 0.5 : 1
                }}
              >
                {submitting ? "Updating..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
